<template>
    <div>
        <!-- Hamburger (only on small screens, only when sidebar is closed) -->
        <div
            class="hamburger"
            v-if="isSmallScreen && !sidebarOpen"
            @click="toggleSidebar"
            :aria-label="$t('nav.openMenu')"
        >
            &#9776;
        </div>
        
        <!-- Backdrop behind the mobile drawer — dims the page and closes on tap -->
        <div
            v-if="isSmallScreen && sidebarOpen"
            class="sidebar-backdrop"
            @click="toggleSidebar"
            aria-hidden="true"
        ></div>

        <!-- Sidebar -->
        <div
            class="sidebar"
            :class="{ 'open': sidebarOpen, 'small-screen': isSmallScreen }"
            v-if="!isSmallScreen || sidebarOpen"
        >
            <!-- Close button (mobile) -->
            <button class="close-btn" @click="toggleSidebar" v-if="isSmallScreen" :aria-label="$t('nav.closeSidebar')">
                <i class="bi bi-x-lg"></i>
            </button>

            <!-- Header -->
            <div class="sidebar-head">
                <img class="sidebar-logo" :src="logo_storalytic" alt="Logo Storalytic" />
                <button class="bell-btn" @click="toggleNotificationsPanel" :aria-label="$t('nav.notifications')">
                    <i class="bi bi-bell-fill"></i>
                    <span v-if="unreadCount > 0" class="notification-badge">{{ unreadCount }}</span>
                </button>

                <!-- wraps to multiple lines as needed -->
                <div class="company-name">{{ companyName }}</div>
            </div>

            <hr class="thin-divider">            
            
            <!-- Navigation links -->
            <div class="main-links">
                <router-link to="/storalytic_demo_store" class="home-link" exact-active-class="home-link-active"><i class="bi bi-house-door"></i> {{ $t('nav.home') }}</router-link>

                <div class="sub-title"><i class="bi bi-graph-up-arrow"></i> {{ $t('nav.insights') }}</div>
                <router-link to="/storalytic_demo_store/store_level" class="nav-link" exact-active-class="active-link" style="padding-left: 20px;">{{ $t('nav.storeLevel') }}</router-link>
                <!-- Promotions is deliberately NOT a fourth tab on Store Level:
                     a promo is user-supplied ground truth about the physical world
                     (price, discount, which SKUs) with its own pinned windows, not a
                     step in the Actions -> Zones -> Explore drill-down. Named
                     "Promotions", never "Actions"-anything — in Dutch "actie" means
                     promotion, and the collision would be total in translation. -->
                <router-link to="/storalytic_demo_store/promotions" class="nav-link" exact-active-class="active-link" style="padding-left: 20px;">{{ $t('nav.promotions') }}</router-link>

                <div v-for="group in groupedZonesByType" :key="group.id">
                    <div v-if="group.zones.some(z => !!z.DetectionZoneName)">
                        <hr class="thin-divider-sub">
                        <div class="expandable-header-detection-zones" @click="toggleType(group.id)">
                             <span :class="{'triangle-icon': true, 'expanded': (expandedTypes[group.id] ?? true)}"></span>
                            {{ $t(`nav.zoneGroups.${group.id}`) }}
                        </div>

                        <div v-if="(expandedTypes[group.id] ?? true)" class="detection-zone-list">
                            <router-link
                                v-for="zone in group.zones"
                                :key="zone.DetectionZoneId"
                                :to="{ name: 'StoralyticDemoDetectionZoneData', params: { zoneName: zone.DetectionZoneName } }"
                                class="flow-link nav-sublink"
                                :class="{ 'disabled-link': !isEnabledZone(zone.DetectionZoneName) }"
                                @click.prevent="!isEnabledZone(zone.DetectionZoneName)"
                                exact-active-class="active-link"
                            >
                                <span class="geom-glyph" :title="zoneGeometryLabel(zone)">
                                    <i v-if="zoneHasPolygon(zone)" class="bi bi-bounding-box-circles"></i>
                                    <i v-if="zoneHasTripwire(zone)" class="bi bi-arrow-left-right"></i>
                                </span>
                                {{ zone.DetectionZoneName }}
                            </router-link>
                        </div>
                    </div>
                    <div v-else>
                        <hr class="thin-divider-sub">
                        <div
                            class="expandable-header-detection-zones disabled-link"
                            :title="$t('demo.nav.typeNotInDemo', { type: $t(`zoneTypes.byId.${group.id}`) })"
                        >
                            <span class="triangle-icon"></span>
                            {{ $t(`nav.zoneGroups.${group.id}`) }}
                        </div>
                    </div>
                </div>

                <hr class="thin-divider">
                <div class="sub-title"><i class="bi bi-shop"></i> {{ $t('nav.realTime') }}</div>
                <p class="nav-link disabled-link" style="padding-left: 20px;">{{ $t('nav.realTimeStoreData') }}</p>

                <hr class="thin-divider">
                
                <div class="sub-title"><i class="bi bi-camera-video"></i> {{ $t('nav.cameras') }}</div>
                
                <p class="nav-link disabled-link" style="padding-left: 20px;">{{ $t('nav.cameraHealthOverview') }}</p>
                
                <div>
                    <!-- Cameras toggle with triangle icon -->
                    <hr class="thin-divider-sub">
                    <div class="expandable-header-cameras" @click="toggleFlowList">
                        <span :class="{'triangle-icon': true, 'expanded': showFlowList}"></span>
                        {{ $t('nav.cameras') }}
                    </div>

                    <!-- Flow names list under Camera Overview, visible only if expanded -->
                    <div v-if="showFlowList" class="flow-list">
                        <p
                            v-for="flow in flows.flows"
                            :key="flow.FlowId"
                            class="flow-link disabled-link"
                        >
                            {{ flow.FlowName }}
                    </p>
                    </div>
                </div>
                
                <!-- External data sources are hidden, as in the production navbar
                     (NavSidebar.vue): weather is part of every store now. -->

                <hr v-if="!isSmallScreen" class="thin-divider">
                <div class="sub-title"><i class="bi bi-node-plus"></i> {{ $t('nav.connectNew') }}</div>
                <p class="nav-link disabled-link" style="padding-left: 20px;">{{ $t('nav.connectNewCamera') }}</p>
            </div>

            <!-- Footer section with connection status, settings, and user info -->
            <div class="sidebar-footer">
                <hr class="thin-divider" />

                <!-- Connection status -->
                <div class="connection-status">
                    <div class="status-label">{{ $t('nav.edgalyticStatusLabel') }}</div>
                    <span class="chip" :class="serverConnection ? 'chip-ok' : 'chip-warn'">
                    <i class="bi" :class="serverConnection ? 'bi-wifi' : 'bi-wifi-off'"></i>
                    {{ serverConnection ? $t('nav.edgalyticConnected') : $t('nav.edgalyticDisconnected') }}
                    </span>
                </div>

                <hr class="thin-divider" />

                <!-- Settings + user row + switch company -->
                <div class="settings-container">
                    <p class="nav-link footer-link disabled-link">
                    <i class="bi bi-gear-fill"></i> {{ $t('nav.settings') }}
                    </p>

                    <div class="user-row">
                        <i class="bi bi-person-fill"></i>
                        <span class="user-name">{{ userName }}</span>
                    </div>

                    <!-- the demo has no account: the choice is remembered by this browser.
                         Hidden until the demo pages are translated. -->
                    <LanguagePicker v-if="DEMO_LANGUAGE_PICKER" :persist="false" />
                </div>
            </div>

        </div>

        <StoralyticDemoNotificationsSidebar v-if="notifCenter.open" :is-visible="notifCenter.open" @close="notifCenter.close()" />

    </div>
</template>
  
<script>
    import { ref, onMounted, onBeforeUnmount, computed, reactive } from 'vue'
    import StoralyticDemoNotificationsSidebar from '@/components/StoralyticDemoNotificationsSidebar.vue'
    import { useNotificationCenterStore } from '@/stores/NotificationCenterStore'
    import { hasAnyTripwire } from '@/utils/tripwireShape'
    import { loadDemoJSON } from '@/api/demoData.js'
    import { useRoute } from 'vue-router'
    import { changeFor, withNewType } from '@/demo/changesTodayDemo'

    import image from "./icons/logo_storalytic.png"
    import LanguagePicker from '@/components/ui/LanguagePicker.vue'
    import { useI18n } from 'vue-i18n'
    // Switch on when the demo pages have their Dutch.
    const DEMO_LANGUAGE_PICKER = true

    export default {
        components: {
            StoralyticDemoNotificationsSidebar,
            LanguagePicker
        },

        setup(props, { emit }) {
            const route = useRoute()
            const { t: tr } = useI18n()
            const user = ref({})
            const flows = ref({})
            const notifications = ref({})
            const detectionZones = ref([])

            const showFlowList = ref(true)
            const toggleFlowList = () => {
                showFlowList.value = !showFlowList.value;

            }

            const expandedTypes = ref({})
            const toggleType = (typeId) => {
                expandedTypes.value[typeId] = !expandedTypes.value[typeId]
            }

            // The notification center (one bell, tabs: Live | From Allen). Panel
            // open/tab state lives in the store so Today's "See all →" can open it too.
            const notifCenter = useNotificationCenterStore()
            notifCenter.seedDemo()
            const toggleNotificationsPanel = () => {
                notifCenter.open ? notifCenter.close() : notifCenter.openTo('live')
            }

            const serverConnection = true

            // State for sidebar visibility and screen size
            const sidebarOpen = ref(false)
            const isSmallScreen = ref(window.innerWidth <= 768)
            const toggleSidebar = () => {
                sidebarOpen.value = !sidebarOpen.value
            }

            const companyName = ref('Newell DIY Store')
            const userName = ref('Demo')

            const ENABLED_PATHS = new Set(['/storalytic_demo_store', '/storalytic_demo_store/store_level'])
            const ENABLED_ZONES = new Set(['Promoted Articles Zone', 'Checkout 1 Interaction', 'Customer Service Desk', 'Garden Utilities'])

            const isEnabledPath = (path) => ENABLED_PATHS.has(path)
            const isEnabledZone = (zoneName) => ENABLED_ZONES.has(zoneName)

            const unreadCount = computed(() => notifCenter.bellCount)

            // ---- Geometry helpers (mirror NavSidebar.vue) ----
            const tryParse = (str) => {
                if (!str) return null
                if (typeof str !== 'string') return str
                try { return JSON.parse(str) } catch { return null }
            }
            const zoneHasPolygon = (zone) => {
                // Demo zones carry their polygon as a parsed array on
                // `Polygon`; production stores it as a JSON string on
                // `DetectionZonePointArray`. Accept either.
                const raw = zone?.Polygon ?? zone?.DetectionZonePointArray
                const pts = (typeof raw === 'string') ? tryParse(raw) : raw
                return Array.isArray(pts) && pts.length >= 3
            }
            const zoneHasTripwire = (zone) => hasAnyTripwire(zone.DetectionZoneTripwirePoints)
            const zoneGeometryLabel = (zone) => {
                const p = zoneHasPolygon(zone), t = zoneHasTripwire(zone)
                if (p && t) return tr('nav.geometry.both')
                if (t) return tr('nav.geometry.tripwireOnly')
                return tr('nav.geometry.polygon')
            }

            const groupedZonesByType = computed(() => {
                // Demo ordering: follows the customer-journey arc — enter,
                // queue, get served, browse, see product highlights. Zones
                // with synthetic data render normally; zones without it
                // render greyed-out at their natural position. Exit Zone (2)
                // and Combined Entrance Exit Zone (3) are intentionally
                // hidden in the demo.
                //
                // NOTE: returning an array (not an object) is intentional.
                // JS objects with integer-string keys iterate in numeric
                // order, which would silently re-sort the array above and
                // park id=10 (Service) between Info and Pick Up regardless
                // of insertion order. An array preserves the order we want.
                const typeOrder = [
                    { id: 1,  name: 'Entrance Zone' },
                    { id: 4,  name: 'Checkout Waiting Zone' },
                    { id: 5,  name: 'Checkout Interaction Zone' },
                    { id: 10, name: 'Service Zone' },
                    { id: 9,  name: 'Info Zone' },
                    { id: 11, name: 'Pick Up Zone' },
                    { id: 6,  name: 'Product Zone' },
                    // Demo + Display sink to the bottom: they carry no demo
                    // sample data, so they render as greyed "available in your
                    // live store" headers below the zones that do.
                    { id: 7,  name: 'Demo Zone' },
                    { id: 8,  name: 'Display Zone' },
                ]

                return typeOrder.map(({ id, name }) => ({
                    id,
                    label: name,
                    zones: detectionZones.value.filter(z => z.DetectionZoneTypeId === id),
                }))
            })

            const loadJSON = (path, opts) => loadDemoJSON(path, opts)

            // Add resize event listener when component is mounted
            onMounted( async () => {
                user.value = await loadJSON('/demo_data/demo_user.json')
                flows.value= await loadJSON('/demo_data/demo_flows.json')
                notifications.value = await loadJSON('/demo_data/demo_notifications.json')
                const _zones = (await loadJSON('/demo_data/demo_detection_zones.json')).detectionzones ?? []
                // ?scenario=type_change: the zone is listed under its NEW type (6.2).
                const _change = changeFor(route.query)
                detectionZones.value = _change ? withNewType(_zones, _change.zone, _change.to) : _zones

                window.addEventListener('resize', handleResize)
                handleResize() // Set initial state based on current window size
            })

            // Handling screen resize
            const handleResize = () => {
                isSmallScreen.value = window.innerWidth <= 768
                if (!isSmallScreen.value) {
                    sidebarOpen.value = true // Keep sidebar open on large screens
                } else {
                    sidebarOpen.value = false // Hide sidebar initially on small screens
                }
            }

            const expanded = reactive({
                detectionZones: true,
                cameras: true
            })
  
            onBeforeUnmount(() => {
                // Remove the resize event listener when component is unmounted
                window.removeEventListener('resize', handleResize)
            })

            return {
                page: 'home',
                logo_storalytic: image,
                serverConnection,
                notifCenter,
                toggleNotificationsPanel,
                unreadCount,
                user,
                userName,
                DEMO_LANGUAGE_PICKER,
                companyName,
                flows,
                toggleFlowList,
                showFlowList,
                groupedZonesByType,
                toggleType,
                expandedTypes,
                zoneHasPolygon,
                zoneHasTripwire,
                zoneGeometryLabel,
                sidebarOpen,
                isSmallScreen,
                toggleSidebar,
                isEnabledPath,
                isEnabledZone,
                expanded,
            }
        }
    }
</script>
  
<style scoped>
/* Backdrop for mobile */
.sidebar-backdrop{
  position:fixed; inset:0; background:rgba(10,14,18,.45);
  z-index:1095;
}

/* Shell */
.sidebar{
  position:fixed; top:0; left:0; z-index:1100;
  width:300px; height:100vh; display:flex; flex-direction:column;
  background:#ffffff;
  border-right:1px solid #e6edf3;
  box-shadow:2px 0 12px rgba(13,38,76,.08);
  padding:12px 12px 10px;
  overflow-y:auto;
  transition:transform .28s ease;
}

/* Mobile slide */
.sidebar.small-screen{ transform:translateX(-102%); height:100vh; }
.sidebar.open.small-screen{ transform:translateX(0); }

/* Close button (mobile) */
.close-btn{
  position:absolute; top:10px; right:12px;
  width:36px; height:36px; border-radius:8px;
  border:1px solid #e6edf3; background:#fff; color:#425461;
  display:inline-flex; align-items:center; justify-content:center;
}
.close-btn:hover{ background:#f0f4f8; }

/* Header band */
.sidebar-head{
  display:grid;
  grid-template-columns: 1fr auto;  /* logo left, bell right */
  grid-template-rows: auto auto;    /* company name on row 2 */
  align-items:center;
  gap:8px 10px;
  padding:6px 6px;
  background:#fff;
  border-radius:10px;
}
.sidebar-logo{
  grid-column:1 / 2;
  grid-row:1 / 2;
  height:80px;              /* cap the logo height */
  width:auto;               /* keep aspect ratio */
  object-fit:contain;
  display:block;
  margin:0;                 /* no extra space */
  margin-left: -15px;
}

.company-name{
  grid-column:1 / -1;       /* span both columns */
  grid-row:2 / 3;
  color:#425461; font-weight:600; font-size:.9rem;
  line-height:1.25;
  white-space:normal;       /* allow wrapping */
  overflow-wrap:anywhere;   /* break long words */
  word-break:break-word;    /* safety */
  margin-top:2px;
}

.bell-btn{
  grid-column:2 / 3;
  grid-row:1 / 2;
  position:relative;
  border:1px solid #e6edf3;
  background:#fff; color:#425461;
  width:36px; height:36px; border-radius:8px;
  display:inline-flex; align-items:center; justify-content:center;
  flex-shrink:0;
}
.bell-btn:hover{ background:#f0f4f8; }
.notification-badge{
  position:absolute; top:-4px; right:-4px;
  background:#C21500; color:#fff; border-radius:999px;
  padding:0 6px; font-size:.65rem; line-height:16px;
}

/* Tighten the divider so links start right after header */
.thin-divider{ border:0; border-top:1px solid #eef2f6; margin:6px 0; }
.thin-divider-sub{ border:0; border-top:1px dashed #e6edf3; margin:2px 14px; }

/* Section titles */
.sub-title{
  font-size:.92rem; color:#4F7EB3; font-weight:700;
  padding:6px 10px 2px; display:flex; align-items:center; gap:8px;
}

/* Home: top-level clickable item, aligned exactly with the section titles
   (.sub-title box model: padding 6px 10px, no horizontal margin -> icon+label
   line up under "Dashboards"). */
.home-link{
  font-size:.92rem; color:#4F7EB3; font-weight:700;
  padding:6px 10px; display:flex; align-items:center; gap:8px;
  text-decoration:none; border-radius:8px; margin:2px 0;
  transition:background .15s ease;
}
.home-link:hover{ background:#f4f7fb; }
.home-link-active{ background:#eef5ff; }

/* Link lists */
.main-links{ padding:0 6px; margin-top:4px;}
.nav-link, .flow-link{
  display:block; text-decoration:none;
  color:#425461; font-size:.86rem; padding:1px 12px; border-radius:8px;
  margin:2px 6px; transition:background .15s ease, color .15s ease;
}
.nav-link:hover, .flow-link:hover{ background:#f4f7fb; color:#4F7EB3; }

/* Active pill */
.active-link{
  color:#4F7EB3 !important; background:#eef5ff;
  border-left:3px solid #B2C149; padding-left:9px;
  font-weight:700;
}

/* Nested lists */
.flow-list, .detection-zone-list{ padding:4px 6px 4px 28px; }

/* Geometry indicator before a zone name */
.geom-glyph{
  display:inline-flex; align-items:center; gap:3px;
  margin-right:6px; color:#7a8a96; font-size:.78rem;
}
.flow-link.active-link .geom-glyph,
.flow-link:hover .geom-glyph{ color:#4F7EB3; }

/* Expandable headers */
.expandable-header-cameras,
.expandable-header-detection-zones{
  display:flex; align-items:center; gap:8px; cursor:pointer;
  color:#425461; font-size:.86rem; padding:1px 12px; margin:0px 6px; border-radius:8px;
}
.expandable-header-cameras:hover,
.expandable-header-detection-zones:hover{ background:#f7f9fb; }
.triangle-icon{
  display:inline-block; width:0; height:0;
  border-left:5px solid transparent; border-right:5px solid transparent; border-top:6px solid #425461;
  transform:rotate(-90deg); transition:transform .2s ease;
}
.triangle-icon.expanded{ transform:rotate(0deg); }

/* Footer */
.sidebar-footer{ margin-top:auto; padding:8px 6px 4px; color:#425461; font-size:.75rem; }
.connection-status{ display:flex; flex-direction:column; align-items:flex-start; gap:6px; }
.status-label{ font-weight:700; color:#425461; }

.chip{
  display:inline-flex; align-items:center; gap:6px;
  border-radius:999px; padding:4px 10px; font-size:.78rem;
  border:1px solid #e6edf3; color:#4F7EB3; background:#f7fbff;
}
.chip-ok{ color:#2c7a3f; background:#f3fbf5; border-color:#d9f1df; }
.chip-warn{ color:#b66a00; background:#fff7e6; border-color:#ffe7bf; }

/* Links aligned with the left edge like the labels/icons above */
.footer-link{
  padding:6px 0; margin:0; border-radius:0;
  color:#425461; font-size:.76rem;
}
.footer-link:hover{ background:transparent; color:#4F7EB3; }

/* user row in one line, wraps nicely if needed */
.settings-container .user-row{
  display:flex; align-items:center; flex-wrap:wrap;
  column-gap:2px;                 /* space between items on same line */
  row-gap:4px;                    /* tight gap when it wraps */
  padding:4px 0;
  color:#425461; font-size:.78rem;
}

/* name wraps naturally */
.settings-container .user-row .user-name{ white-space:normal; }

.settings-container .user-row i.bi-person-fill{
  margin-right: 1px; /* explicit icon gap before the name */
}

/* small gap around the dot */
.settings-container .user-row .dot{
  color:#9aa8b3;
  margin:0 6px;
}

/* button styled as a link; pointer cursor */
.settings-container .user-row .logout-link-btn{
  appearance:none; -webkit-appearance:none;
  background:transparent; border:0; padding:0;
  color:#4F7EB3; font:inherit; cursor:pointer;
  line-height:1.2;
}
.settings-container .user-row .logout-link-btn:hover{ text-decoration:underline; }

/* if you MUST keep <a class="logout-link"> instead of the button, enforce inline */
.settings-container .user-row .logout-link{
  display:inline !important;
  margin:0 !important; padding:0 !important;
  color:#4F7EB3; cursor:pointer; text-decoration:none;
}
.settings-container .user-row .logout-link:hover{ text-decoration:underline; }

/* Top bar icons (mobile) */
.hamburger{
  position:fixed; top:10px; left:10px; z-index:1100;
  font-size:26px; color:#425461; cursor:pointer;
}
.mobile-bell{
  position:fixed; top:12px; left:54px; z-index:1100; color:#425461; cursor:pointer;
}
.notification-bell{ position:relative; }

.disabled-link {
  color: #9ca3af !important;     /* light grey */
  font-style: italic;
  pointer-events: none;           /* visually and functionally disabled */
  opacity: 0.8;
}
.nav-link,
.nav-sublink {
  display: block;
  padding: 3px 10px;
  text-decoration: none;
}
.submenu-title { cursor: default; }
.submenu { list-style: none; margin: 6px 0 0; padding-left: 12px; }

/* Scrollbar (webkit) */
.sidebar::-webkit-scrollbar{ width:10px; }
.sidebar::-webkit-scrollbar-thumb{ background:#e6edf3; border-radius:10px; }
.sidebar::-webkit-scrollbar-thumb:hover{ background:#d7e2ee; }

/* Responsive width */
@media (max-width: 992px){ .sidebar{ width:280px; } }
@media (max-width: 768px){ 
    .sidebar{ width:56vw; }

    /* move the close a hair inward & smaller */
    .close-btn{
        top: 8px;
        right: 8px;
        width: 32px;
        height: 32px;
    }

    /* reserve space on the right of the header equal to the close button width */
    .sidebar-head{
        padding-right: 56px;   /* 32px button + ~24px breathing room */
    }

    /* nudge/smaller bell so it never bumps the close button */
    .bell-btn{
        width: 32px;
        height: 32px;
        margin-right: 4px;      /* tiny inset from the header edge */
        justify-self: end;      /* keep it at the right of grid column */
    }

    .notification-badge{
        top: -3px;
        right: -3px;
    }
}

/* Greyed-out zone-type tease header (lock chip on the right). Used for
   zone types that aren't backed by demo synthetic data — keeps the viewer
   aware they exist as features in the live product. */
.zone-type-tease-lock{
    margin-left: 6px;
    font-size: 0.75em;
    opacity: 0.6;
}
.expandable-header-detection-zones.disabled-link{
    cursor: not-allowed;
    pointer-events: auto;
}
.expandable-header-detection-zones.disabled-link .triangle-icon{
    opacity: 0.4;
}
</style>